import { useLayoutEffect, useState } from "react";
import { createPortal } from "react-dom";
import { Library } from "lucide-react";
import MenuIconTooltip from "./MenuIconTooltip";
import { useAuth } from "./AuthGate";
import { FRAME_LAUNCHER_TOOLTIPS, MENU_LIBRARY_TOOLTIPS } from "../constants/menuTooltips";
import { getAppPortalRoot } from "../utils/appPortal";
import { getAppScale } from "../utils/appScale";
import "./LibraryLauncher.css";

function readFrameBox() {
  const frame = document.querySelector(".custom-pattern");
  if (!frame) return { left: 0, top: 0, width: 0, height: 0, scale: 1 };
  const rect = frame.getBoundingClientRect();
  return {
    left: rect.left,
    top: rect.top,
    width: rect.width,
    height: rect.height,
    scale: getAppScale(),
  };
}

export default function LibraryLauncher({
  active = false,
  reading = false,
  onOpen = () => {},
  onOpenCharacters = () => {},
}) {
  const { user } = useAuth();
  const [frameBox, setFrameBox] = useState({
    left: 0,
    top: 0,
    width: 0,
    height: 0,
    scale: 1,
  });

  useLayoutEffect(() => {
    const update = () => setFrameBox(readFrameBox());
    update();
    const frame = document.querySelector(".custom-pattern");
    const observer =
      frame && typeof ResizeObserver !== "undefined" ? new ResizeObserver(update) : null;
    if (frame && observer) observer.observe(frame);
    window.addEventListener("resize", update);
    window.visualViewport?.addEventListener("resize", update);
    window.visualViewport?.addEventListener("scroll", update);
    return () => {
      observer?.disconnect();
      window.removeEventListener("resize", update);
      window.visualViewport?.removeEventListener("resize", update);
      window.visualViewport?.removeEventListener("scroll", update);
    };
  }, []);

  if (!user?.sub) return null;
  const portalRoot = getAppPortalRoot();
  if (!portalRoot) return null;

  const characters = reading || active;
  const scale = frameBox.scale || 1;
  const toggleStyle = {
    left: `${frameBox.left}px`,
    top: `${frameBox.top + frameBox.height / 2 - 117 * scale}px`,
  };
  const tooltip = characters ? MENU_LIBRARY_TOOLTIPS.characters : FRAME_LAUNCHER_TOOLTIPS.library;

  return createPortal(
    <div className="library-launcher">
      <MenuIconTooltip
        tooltip={tooltip}
        placement="below"
        portal
        className="library-launcher__tip"
        style={toggleStyle}
      >
        <button
          type="button"
          className="library-launcher__toggle"
          aria-label={tooltip}
          aria-pressed={characters ? undefined : active}
          onClick={characters ? onOpenCharacters : onOpen}
        >
          {characters ? (
            <span className="library-launcher__character" aria-hidden="true" />
          ) : (
            <Library className="library-launcher__icon" strokeWidth={1} aria-hidden="true" />
          )}
        </button>
      </MenuIconTooltip>
    </div>,
    portalRoot
  );
}
